
import {
  createAddressBookItem,
  editAddressBookItem,
  getAddressBooks,
  lookupCustomer,
  moveAddressBookItem,
  removeAddressBookItem,
  putAddressBookDefault,
} from '@/api';
import LookupCustomerCode from '@/components/jobBooking/AdvancedJobBooking/LookupCustomerCode.vue';
import Header from '@/components/jobBooking/Header/Header.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import AddressesTable from '@/components/addressBook/addressesTable/AddressesTable.vue';

import { User } from '@/models/main';
import { sendError } from '@/helpers/sentry';
import { AddressBookEndpointResponse } from '@/models/addressBook';
import { Component, Vue } from 'vue-property-decorator';
import { mapState } from 'vuex';

@Component({
  components: {
    AddressesTable,
    BackButton,
    Header,
    LookupCustomerCode,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class AddressBook extends Vue {
  private loading = false;
  private user: User;
  private autocompleteCustomer = lookupCustomer;
  private customerCode = '';
  private username = '';
  private usernameErrors: string[] = [];

  private userAddresses = [];
  private userWarehouses = [];
  private userAdressesData = {
    name: '',
    id: -1,
  };

  private allAddresses: AddressBookEndpointResponse[] = [];
  private allAddressesLoading = false;
  private customAddresses: AddressBookEndpointResponse[] = [];
  private customAddressesLoading = false;
  private panelAll: number[] = [];
  private panelCustom: number[] = [];

  private dialog = false;
  private dialogDelete = false;

  private reorderAddress(id, position, context): void {
    moveAddressBookItem(id, position + 1)
      .then(res => {
        if (res.status === 200) {
          this.getAddressBooksWrapper('', this.username, this.customerCode ? this.customerCode : '', 1, context);
        } else {
          throw new Error(`res.status: ${res.status}`);
        }
      })
      .catch(err => {
        sendError(err, 'reorderAddress');
      });
  }

  private setCustomerCode(customerCode): void {
    if (customerCode) {
      this.customerCode = customerCode;
      this.username = '';
      this.usernameErrors = [];
      this.panelAll = [];
      this.getAddressBooksWrapper('', '', customerCode, 1, 'custom');
      this.panelCustom = [0];
    }
  }

  private setUsername(): void {
    if (this.username === '') {
      this.usernameErrors = ['Enter the username'];
      this.panelAll = [0];
    } else if (this.username === this.user.username) {
      this.usernameErrors = ['This is your account'];
      this.panelAll = [0];
    } else {
      this.usernameErrors = [];
      this.clearCustomAddresses();
      this.setCustomerCode('');
      this.panelAll = [];
      this.getAddressBooksWrapper('', this.username, '', 1, 'custom');
      this.panelCustom = [0];
    }
  }

  private clearCustomAddresses(): void {
    this.customerCode = '';
    this.customAddresses = [];
    this.panelAll = [0];
  }

  private addAddress(address, id, context): void {
    const params = {
      suburb: address.suburbId,
      addressbook: id,
      name: address.name,
      line1: address.line1,
      line2: address.line2,
      line3: address.line3,
      note_phone: address.note_phone,
      note_text: address.note_text,
      note_name: address.note_name,
      city: '',
      warehouse: false,
    };

    createAddressBookItem(params)
      .then(res => {
        if (res.status === 201) {
          this.getAddressBooksWrapper('', this.username, this.customerCode ? this.customerCode : '', 1, context);
        } else {
          throw new Error(`res.status: ${res.status}`);
        }
      })
      .catch(err => {
        sendError(err, 'addAddress');
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private editAddress(address, id, context): void {
    const params = {
      id: address.id,
      suburb: address.suburbId,
      addressbook: id,
      name: address.name,
      line1: address.line1,
      line2: address.line2,
      line3: address.line3,
      note_phone: address.note_phone,
      note_text: address.note_text,
      note_name: address.note_name,
      city: '',
      warehouse: false,
    };

    editAddressBookItem(
      params,
      context === 'custom' ? this.username : '',
      context === 'custom' ? this.customerCode : '',
    )
      .then(res => {
        if (res.status === 200) {
          this.getAddressBooksWrapper(
            '',
            this.username,
            context === 'custom' && this.customerCode ? this.customerCode : '',
            1,
            context,
          );
        } else {
          throw new Error(`res.status: ${res.status}`);
        }
      })
      .catch(err => {
        sendError(err, 'editAddress');
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private removeAddress(id, context): void {
    removeAddressBookItem(id, context === 'custom' ? this.username : '', context === 'custom' ? this.customerCode : '')
      .then(res => {
        if (res.status === 204) {
          this.getAddressBooksWrapper(
            '',
            context === 'custom' && this.username ? this.username : '',
            context === 'custom' && this.customerCode ? this.customerCode : '',
            1,
            context,
          );
        } else {
          throw new Error(`res.status: ${res.status}`);
        }
      })
      .catch(err => {
        sendError(err, 'removeAddress');
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private setDefaultAddress(id, context, bookCustomerCode): void {
    putAddressBookDefault(id, bookCustomerCode)
      .then(res => {
        if (res.status === 200) {
          this.getAddressBooksWrapper(
            '',
            this.username,
            context === 'custom' && this.customerCode ? this.customerCode : '',
            1,
            context,
          );
        } else {
          throw new Error(`res.status: ${res.status}`);
        }
      })
      .catch(err => {
        sendError(err, 'setDefaultAddress');
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private getAddressBooksWrapper(exclude_book, username, customer, page, context): void {
    if (context === 'custom') {
      this.customAddressesLoading = true;
    } else {
      this.allAddressesLoading = true;
    }

    const params = {
      exclude_book: exclude_book || '',
      username: username || '',
      customer: customer || '',
      page: page || 1,
    };

    getAddressBooks(params)
      .then(res => {
        if (context === 'custom') {
          this.customAddresses = res;
          this.customAddressesLoading = false;
        } else {
          this.allAddresses = res;
          this.allAddressesLoading = false;
        }
      })
      .catch(() => {
        this.usernameErrors = ['There is no such account'];
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private mounted(): void {
    this.loading = true;
    this.customAddresses = [];
    this.getAddressBooksWrapper('', '', '', 1, '');
    this.panelAll = [0];
  }
}
